import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule, FormControlName } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { AuthService } from '../../@services/auth.service';
import { UsersService } from '../../@services/users.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { NgxCsvParserModule, NgxCsvParser } from 'ngx-csv-parser';
import { ToastService } from '../../@services/toast.service';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';
import { ScannersService } from '../../@services/scanners.service';
import { BillingService } from '../../@services/billing.service';

@Component({
  selector: 'app-user-tracking',
  imports: [AgGridAngular, CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './user-tracking.component.html',
  styleUrl: './user-tracking.component.scss'
})
export class UserTrackingComponent {
  subs: any = [];
  gridDarkTheme = agGridThemes.dark;
  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];
  selected_user: any;
  theme: any;
  scanners: any[] = [];
  months: any[] = [];
  single_scanner_search: any = false;
  customers: any[] = [
    { id: 'alpha-shark', name: 'Alpha Shark'},
    { id: 'delphi-sig', name: 'Delphi Signal'},
    { id: 'mta', name: 'MTA'},
    { id: 'option-pit', name: 'Option Pit'},
    { id: 'simpler-trading', name: 'Simpler Trading'},
    { id: 'strat-alerts', name: 'Strat Alerts'},
    { id: 'theo-trade', name: 'Theo Trade'},
  ];
  types: any[] = [
    { id: 'activations', name: 'Activations'},
    { id: 'deactivations', name: 'Deactivations'}
  ];

  filterScanners = new FormControl([]);
  filterCustomers = new FormControl<string[]>([]);
  filterMonths = new FormControl([]);
  // Default to BOTH types selected so the initial auto-load actually
  // returns rows. Backend get_user_tracking() only fetches when
  // types.includes('activations') OR types.includes('deactivations') —
  // an empty list makes it return []. Mike 2026-07-06.
  filterTypes = new FormControl<string[]>(['activations', 'deactivations']);

  constructor(private authService: AuthService,
    private adminService: UsersService,
    private billingService : BillingService,
    private scannersService: ScannersService
  ) {
    this.init_grid();
    this.theme = this.gridDarkTheme;
  }

  async ngOnInit() {
    this.generate_months();
    await this.get_scanners();
    // Auto-load the tracking roster so the panel isn't a No-Rows-To-Show
    // wall on first navigation — matches billing.component's fix
    // (feedback_user_display_reads_cache_never_cognito applies here too;
    // the underlying get_user_tracking is Mongo-based so no pool walk
    // needed, but the auto-load rule holds). Mike 2026-07-06.
    await this.get_user_tracking();
  }

  ngOnDestroy(): void {}

  async get_user_tracking() {
    let query: any = {};
    if (this.filterScanners.value && this.filterScanners.value.length > 0) query.scanners = this.filterScanners.value;
    if (this.filterCustomers.value && this.filterCustomers.value.length > 0) query.customers = this.filterCustomers.value;
    if (this.filterMonths.value && this.filterMonths.value.length > 0) query.months = this.filterMonths.value;
    if (this.filterTypes.value && this.filterTypes.value.length > 0) query.types = this.filterTypes.value;
    let res: any = await this.billingService.getUserTracking(query);

    if (query.scanners && query.scanners.length === 1) {
      this.single_scanner_search = true;
    }
    else {
      this.single_scanner_search = false;
    }
    
    if (res && !res.err) {
      this.data = res;
      console.log(this.data);
    }
    else {
      this.data = [];
    }
  }

  async get_scanners() {
    this.scanners = [];
    let res = await this.scannersService.getScanners();
    console.log(res);
    if (res && !res.err) this.scanners = res;
  }

  async refresh() {
    this.selected_user = null;
    await this.get_user_tracking();
  }

  async clear_filters() {
    // Reset form controls
    this.filterScanners.reset([]);
    this.filterCustomers.reset([]);
    this.filterMonths.reset([]);
    this.filterTypes.reset([]);

    // Clear grid filters
    if (this.grid.api) {
      this.grid.api.setFilterModel(null);
    }
  }

  generate_months() {
    const start = new Date(2025, 2);
    const now = new Date();
    const avail_months: { id: string; name: string }[] = [];

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Generate available months (June 2025 start)
    let current_month = new Date(start);
    while (current_month <= now) {
      const year = current_month.getFullYear();
      const month = current_month.getMonth();

      const id = `${year}-${String(month + 1).padStart(2, '0')}`;
      const name = `${monthNames[month]} ${year}`;

      avail_months.push({ id, name });
      current_month.setMonth(month + 1);
    }

    this.months = avail_months;
  }


  /**** Users Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          // filter: 'agTextColumnFilter',
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.email;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "active",
          headerName: 'Enabled',
          width: 95,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else if (params.value === false) {
              return '<i class="fa fa-circle-xmark text-red-800"></i>';
            }
            else {
              return '?';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: [true, false]
              }
            }
          },
        },
        {
          field: "email",
          headerName: 'Email',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "customer",
          headerName: 'Customer',
          minWidth: 100,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "scanners",
          headerName: 'Scanners',
          minWidth: 108,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            let scanner_ids = params.value || [];
            let scanner_names = ``;
            for (const scanner of scanner_ids) {
              if (!scanner) return;
              scanner_names += `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${scanner}</span>`;
            };

            return `<div class="flex items-center h-full">
              <div class="flex-1 flex gap-1">${scanner_names}</div>
            </div>`
          },
          editable: true,
        },
        {
          field: "start_date",
          headerName: 'Activation Date',
          filter: 'agDateColumnFilter',
          width: 210,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['start_date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "end_date",
          headerName: 'Deactivation Date',
          filter: 'agDateColumnFilter',
          width: 210,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['end_date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
      ]
    }
  }
  on_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_data = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_user = e.node.data;
    }

    //Force refresh of scanners column to show edit button
    this.grid.api.refreshCells({
      columns: ['attributes.custom:scanners'],
      force: true
    });
  }
  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        case 'edit-scanners':
          //this.on_edit_user_scanners(user);
          break;
        default:
          break;
      }
    }
  }
  async get_scanner_values() {
    if (!this.scanners || this.scanners.length === 0) {
      await this.get_scanners();
    }

    if (!this.scanners.length) return [];

    return this.scanners.map((s: any) => {
      return s.Id;
    });
  }
}
